import { useCallback, useEffect, useState } from 'react'
import { api } from '../lib/api'
import type {
  TopUpCreatedResponse,
  TopUpResponse,
  Wallet,
  WalletEntriesPageResponse,
  WalletEntry,
  WalletResponse,
} from '../lib/types'

/**
 * How a top-up wait ended. `credited` means the ledger credit is written — the
 * ONLY signal the client may treat as money arrived (build-contract §3.7).
 * `pending` means we gave up polling while Stripe was still settling; the
 * webhook/reconcile jobs will land it server-side eventually.
 */
export type TopUpWaitOutcome = 'credited' | 'failed' | 'canceled' | 'pending'

interface UseWalletOptions {
  /**
   * The wallet endpoints 404 (`feature_disabled`) when the server's
   * group_wallet flag is off — pass `features.group_wallet` so a flagged-off
   * client never fetches them.
   */
  enabled?: boolean
}

interface AwaitTopUpOptions {
  tries?: number
  delayMs?: number
}

/**
 * Personal wallet state for the /wallet screen (build-contract §3.7): the
 * server-truth balance, the signed append-only ledger (paged), and the two
 * top-up actions. All money truth stays server-side — this hook renders what
 * the ledger says and never infers a balance from Stripe's client responses.
 */
export function useWallet({ enabled = true }: UseWalletOptions = {}) {
  const [wallet, setWallet] = useState<Wallet | null>(null)
  const [entries, setEntries] = useState<WalletEntry[]>([])
  const [entriesPage, setEntriesPage] = useState(1)
  const [entriesHasMore, setEntriesHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(enabled)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /** Reloads the balance and resets the ledger to its first page. */
  const refresh = useCallback(() => {
    if (!enabled) return Promise.resolve()
    return Promise.all([
      api.get<WalletResponse>('/wallet'),
      api.get<WalletEntriesPageResponse>('/wallet/entries?page=1'),
    ])
      .then(([walletData, entriesData]) => {
        setWallet(walletData.wallet)
        setEntries(entriesData.entries)
        setEntriesPage(entriesData.page)
        setEntriesHasMore(entriesData.has_more)
        setError(null)
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Could not load your wallet.')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [enabled])

  useEffect(() => {
    refresh()
  }, [refresh])

  const loadMoreEntries = useCallback(async () => {
    if (!enabled || isLoadingMore || !entriesHasMore) return
    setIsLoadingMore(true)
    try {
      const data = await api.get<WalletEntriesPageResponse>(
        `/wallet/entries?page=${entriesPage + 1}`,
      )
      setEntries((prev) => [...prev, ...data.entries])
      setEntriesPage(data.page)
      setEntriesHasMore(data.has_more)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load more history.')
    } finally {
      setIsLoadingMore(false)
    }
  }, [enabled, entriesHasMore, entriesPage, isLoadingMore])

  /**
   * Phase 1 of a top-up: creates the PaymentIntent server-side and hands back
   * the Elements client secret plus our top-up id (the id rides the 3DS
   * return_url so the /wallet landing knows what to poll).
   */
  const createTopUp = useCallback(async (amountCents: number) => {
    const data = await api.post<TopUpCreatedResponse>('/wallet/top_ups', {
      amount_cents: amountCents,
    })
    return { topUpId: data.top_up.id, clientSecret: data.client_secret }
  }, [])

  /**
   * Polls GET /wallet/top_ups/:id until `credited` flips true or the top-up
   * lands terminally failed/canceled (the endpoint itself re-checks the
   * PaymentIntent while pending, so this doubles as the webhook fallback).
   * Always refreshes the wallet afterwards so the UI shows the ledger truth.
   */
  const awaitTopUpCredited = useCallback(
    async (
      topUpId: string,
      { tries = 10, delayMs = 2000 }: AwaitTopUpOptions = {},
    ): Promise<TopUpWaitOutcome> => {
      let outcome: TopUpWaitOutcome = 'pending'
      for (let i = 0; i < tries; i += 1) {
        const data = await api.get<TopUpResponse>(`/wallet/top_ups/${topUpId}`)
        if (data.top_up.credited) {
          outcome = 'credited'
          break
        }
        if (data.top_up.status === 'failed' || data.top_up.status === 'canceled') {
          outcome = data.top_up.status
          break
        }
        if (i < tries - 1) {
          await new Promise((resolve) => setTimeout(resolve, delayMs))
        }
      }
      await refresh()
      return outcome
    },
    [refresh],
  )

  return {
    wallet,
    entries,
    entriesHasMore,
    isLoading,
    isLoadingMore,
    error,
    refresh,
    loadMoreEntries,
    createTopUp,
    awaitTopUpCredited,
  }
}
